import { useAuth } from '../context/AuthContext';

/**
 * Hook para obtener el rol del usuario autenticado.
 *
 * NER-4257 follow-up (roadmap QA 2026-09-10 #2): antes este hook hacía su
 * propio fetchAuthSession({forceRefresh:true}) en cada montaje. Como se monta
 * en varios lugares a la vez (ProtectedRoute, Sidebar, ActivityBar,
 * UnauthorizedPage, ...), eso disparaba múltiples refresh de token
 * concurrentes e independientes — una condición de carrera real: una
 * instancia podía resolver con rol vacío (mandando a /unauthorized) mientras
 * otra, milisegundos/segundos después, resolvía con el rol correcto
 * (disparando el auto-redirect de vuelta a '/'). De ahí el "flash" de Acceso
 * No Autorizado antes de redirigir.
 *
 * Ahora el rol se deriva una sola vez, dentro de AuthContext.checkUser(), a
 * partir de la misma sesión que ya se usa para resolver user/organization.
 * Este hook es un wrapper fino sobre ese estado compartido — misma forma de
 * retorno que antes, para no tener que tocar a ningún consumidor.
 */
export const useUserRole = () => {
  const { role, groups, loading, roleWasDefaulted } = useAuth();

  /**
   * Verifica si el usuario tiene uno de los roles permitidos
   * @param {string[]} allowedRoles - Array de roles permitidos
   * @returns {boolean}
   */
  const hasRole = (allowedRoles) => {
    if (!role) return false;
    return allowedRoles.includes(role);
  };

  /**
   * Verifica si el usuario es OWNER
   * @returns {boolean}
   */
  const isOwner = () => role === 'OWNER';

  /**
   * Verifica si el usuario es ADMIN o superior
   * @returns {boolean}
   */
  const isAdmin = () => ['OWNER', 'ADMIN'].includes(role);

  /**
   * Verifica si el usuario puede acceder al admin panel
   * @returns {boolean}
   */
  const canAccessAdminPanel = () => ['OWNER', 'ADMIN'].includes(role);

  return {
    role,
    groups,
    loading,
    // El error de fetch de sesión, si lo hay, ya lo maneja AuthContext (y
    // ningún consumidor de este hook lee este campo); se mantiene por
    // compatibilidad de forma con el shape anterior del hook.
    error: null,
    hasRole,
    isOwner,
    isAdmin,
    canAccessAdminPanel,
    // true cuando no llego ni grupo Cognito ni custom:role, y el rol
    // 'COMANDA' es solo el fallback por defecto (no un rol real asignado).
    roleWasDefaulted,
  };
};
